import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { trackSignIn } from '../lib/analytics'
import { APP_NAME } from '../lib/constants'
import toast from 'react-hot-toast'

export function SignIn() {
  const navigate = useNavigate()
  const { signIn } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await signIn(email, password)
      
      // Show success toast
      toast.success(" دلوقتي تقدر تضيف كلمة وتتفاعل مع الموقع")
      
      // Track successful sign in
      trackSignIn('email')
      
      navigate('/')
    } catch (err) {
      console.error('Sign in error:', err)
      const errorMessage = err instanceof Error ? err.message : String(err)
      if (errorMessage.includes('Invalid login credentials')) {
        setError('البريد الإلكتروني أو كلمة المرور غير صحيحة')
      } else {
        setError('حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12 bg-slate-900">
      <div className="w-full max-w-md space-y-8">
        <PageHeader />
        <SignInFormCard
          email={email}
          password={password}
          loading={loading}
          error={error}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={handleSubmit}
        />
        <BackToHomeLink />
      </div>
    </div>
  )
}

function PageHeader() {
  return (
    <div className="text-center">
      <h1 className="mb-2 text-4xl font-bold text-amber-500">{APP_NAME.ar}</h1>
      <h2 className="mb-2 text-2xl font-semibold text-slate-100">تسجيل الدخول</h2>
      <p className="text-slate-400">مرحبًا بعودتك! سجل دخولك للمتابعة</p>
    </div>
  )
}

interface SignInFormCardProps {
  email: string
  password: string
  loading: boolean
  error: string | null
  onEmailChange: (email: string) => void
  onPasswordChange: (password: string) => void
  onSubmit: (e: React.FormEvent) => void
}

function SignInFormCard({
  email,
  password,
  loading,
  error,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: SignInFormCardProps) {
  return (
    <div className="p-8 border rounded-lg bg-slate-800 border-slate-700">
      <form onSubmit={onSubmit} className="space-y-6">
        {error && <ErrorMessage message={error} />}
        <EmailField value={email} onChange={onEmailChange} disabled={loading} />
        <PasswordField value={password} onChange={onPasswordChange} disabled={loading} />
        <SubmitButton loading={loading} />
      </form>
      <SignUpPrompt />
    </div>
  )
}

interface ErrorMessageProps {
  message: string
}

function ErrorMessage({ message }: ErrorMessageProps) {
  return (
    <div className="p-4 border rounded-lg bg-rose-500/10 border-rose-500/50">
      <p className="text-sm text-rose-400">{message}</p>
    </div>
  )
}

interface EmailFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function EmailField({ value, onChange, disabled }: EmailFieldProps) {
  return (
    <div>
      <label htmlFor="email" className="block mb-2 text-sm font-medium text-slate-300">
        البريد الإلكتروني
      </label>
      <input
        id="email"
        type="email"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-left border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        placeholder="your@email.com"
        disabled={disabled}
      />
    </div>
  )
}

interface PasswordFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function PasswordField({ value, onChange, disabled }: PasswordFieldProps) {
  return (
    <div>
      <label htmlFor="password" className="block mb-2 text-sm font-medium text-slate-300">
        كلمة المرور
      </label>
      <input
        id="password"
        type="password"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-left border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        placeholder="••••••••"
        disabled={disabled}
      />
    </div>
  )
}

interface SubmitButtonProps {
  loading: boolean
}

function SubmitButton({ loading }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full px-4 py-3 font-semibold transition-colors duration-200 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
    </button>
  )
}

function SignUpPrompt() {
  return (
    <div className="mt-6 text-center">
      <p className="text-slate-400">
        ليس لديك حساب؟{' '}
        <Link
          to="/signup"
          className="font-semibold transition-colors text-amber-500 hover:text-amber-400"
        >
          إنشاء حساب جديد
        </Link>
      </p>
    </div>
  )
}

function BackToHomeLink() {
  return (
    <div className="text-center">
      <Link
        to="/"
        className="text-sm transition-colors text-slate-400 hover:text-slate-300"
      >
        ← العودة للصفحة الرئيسية
      </Link>
    </div>
  )
}
